'use client'

import * as React from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { DynamicIcon } from 'lucide-react/dynamic'
import Fuse from 'fuse.js'
import { useDebounceValue } from 'usehooks-ts'
import { iconsData } from './icons-data'
import type { IconName } from 'lucide-react/dynamic'

import type { IconData } from './icons-data'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  EmojiPicker,
  EmojiPickerContent,
  EmojiPickerSearch,
} from '@/components/app/pickers/emoji-picker'
import { normalizeIconName } from '@/components/app/pickers/icon-picker'
import { ProjectIcon } from '@/lib/utils/projectIcon'
import { cn } from '@/lib/utils'

const ICONS_PER_ROW = 5

export interface UnifiedIconValue {
  // `null` icon/iconType represents a color-only selection (no symbol yet) — the
  // consumer renders the letter-badge fallback tinted with `iconColor`.
  iconType: string | null
  icon: string | null
  iconColor?: string | null
}

export interface UnifiedIconPickerProps {
  iconType: string | null
  icon: string | null
  iconColor?: string | null
  onValueChange: (value: UnifiedIconValue | null) => void
  children?: React.ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  sideOffset?: number
}

/**
 * Unified icon picker with tabs for Emojis, Icons, and Custom image upload.
 */
export function UnifiedIconPicker({
  iconType,
  icon,
  iconColor,
  onValueChange,
  children,
  side,
  align = 'start',
  sideOffset,
}: UnifiedIconPickerProps) {
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<string>(() => {
    if (iconType === 'emoji') return 'emojis'
    if (iconType === 'custom') return 'custom'
    return 'icons'
  })

  const [localIconColor, setLocalIconColor] = useState<string | null>(iconColor ?? null)

  // Sync local color state when prop changes
  React.useEffect(() => {
    setLocalIconColor(iconColor ?? null)
  }, [iconColor])

  const handleEmojiSelect = useCallback(
    (emoji: string) => {
      onValueChange({ iconType: 'emoji', icon: emoji, iconColor: null })
      setOpen(false)
    },
    [onValueChange],
  )

  const handleIconSelect = useCallback(
    (iconName: IconName) => {
      onValueChange({ iconType: 'icon', icon: iconName, iconColor: localIconColor })
      setOpen(false)
    },
    [onValueChange, localIconColor],
  )

  const handleColorChange = useCallback(
    (color: string | null) => {
      setLocalIconColor(color)
      // Emit immediately so the change is picked up (and marks the form dirty)
      // even before a symbol is chosen. With a Lucide symbol already set, keep it
      // and just recolor the background; with no symbol, emit a color-only value
      // so the letter-badge fallback renders tinted with this background color.
      if (icon && (iconType === 'icon' || !iconType)) {
        onValueChange({ iconType: iconType || 'icon', icon, iconColor: color })
      } else if (!icon) {
        onValueChange({ iconType: null, icon: null, iconColor: color })
      }
    },
    [icon, iconType, onValueChange],
  )

  const handleClear = useCallback(() => {
    onValueChange(null)
    setOpen(false)
  }, [onValueChange])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children || (
          <Button variant="outline" size="icon" className="size-8">
            <ProjectIcon iconType={iconType} icon={icon} iconColor={iconColor} size={16} />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" side={side} align={align} sideOffset={sideOffset}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="gap-0">
          <div className="flex items-center justify-between border-b px-3 py-2">
            <TabsList className="h-8">
              <TabsTrigger value="emojis" className="text-xs px-3">
                Emojis
              </TabsTrigger>
              <TabsTrigger value="icons" className="text-xs px-3">
                Icons
              </TabsTrigger>
              <TabsTrigger value="custom" className="text-xs px-3">
                Custom
              </TabsTrigger>
            </TabsList>
            {(icon || iconType) && (
              <Button
                variant="ghost"
                size="icon"
                className="size-7 text-muted-foreground hover:text-destructive"
                onClick={handleClear}
              >
                <Trash2 className="size-4" />
              </Button>
            )}
          </div>

          <TabsContent value="emojis" className="mt-0">
            <div className="h-[280px]">
              <EmojiPicker
                className="h-full"
                onEmojiSelect={(emoji) => handleEmojiSelect(emoji.emoji)}
              >
                <EmojiPickerSearch placeholder={t('iconPicker.searchEmoji')} />
                <EmojiPickerContent />
              </EmojiPicker>
            </div>
          </TabsContent>

          <TabsContent value="icons" className="mt-0">
            <IconGridTab
              currentIcon={iconType === 'icon' || !iconType ? icon : null}
              onSelect={handleIconSelect}
            />
          </TabsContent>

          <TabsContent value="custom" className="mt-0">
            <div className="flex flex-col items-center justify-center gap-3 p-6 h-[280px] text-center">
              <div className="text-muted-foreground text-sm">
                Custom image upload coming soon
              </div>
              <p className="text-xs text-muted-foreground/70">
                You'll be able to upload a custom image as your project icon.
              </p>
            </div>
          </TabsContent>
        </Tabs>

        {/* Icon color picker — only for Lucide icons */}
        {(iconType === 'icon' || !iconType || activeTab === 'icons') && iconType !== 'emoji' && iconType !== 'custom' && (
          <div className="border-t px-3 py-3">
            <p className="text-xs font-medium text-muted-foreground mb-2">Icon color</p>
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => handleColorChange(null)}
                className={cn(
                  'relative size-6 rounded-md border border-border bg-background transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
                )}
                aria-label="Default color"
              >
                {localIconColor === null && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="size-2 rounded-full bg-foreground/50" />
                  </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-[1px] h-3.5 bg-muted-foreground/40 rotate-45" />
                </div>
              </button>
              {[
                '#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16', '#22C55E',
                '#10B981', '#14B8A6', '#06B6D4', '#0EA5E9', '#3B82F6', '#6366F1',
                '#8B5CF6', '#A855F7', '#D946EF', '#EC4899', '#F43F5E', '#64748B',
              ].map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => handleColorChange(color)}
                  className="relative size-6 rounded-md transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
                  style={{ backgroundColor: color }}
                  aria-label={`Select color ${color}`}
                >
                  {localIconColor === color && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="size-2 rounded-full bg-card" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

/**
 * Icon grid tab — search + virtualized Lucide icon grid
 */
function IconGridTab({
  currentIcon,
  onSelect,
}: {
  currentIcon: string | null
  onSelect: (iconName: IconName) => void
}) {
  const { t } = useTranslation('common')
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebounceValue(search, 150)
  const parentRef = useRef<HTMLDivElement>(null)

  const fuse = useMemo(
    () =>
      new Fuse(iconsData, {
        keys: ['name', 'tags'],
        threshold: 0.3,
      }),
    [],
  )

  const filteredIcons = useMemo(() => {
    if (!debouncedSearch.trim()) return iconsData
    return fuse.search(debouncedSearch).map((r) => r.item)
  }, [debouncedSearch, fuse])

  const rows = useMemo(() => {
    const result: Array<Array<IconData>> = []
    for (let i = 0; i < filteredIcons.length; i += ICONS_PER_ROW) {
      result.push(filteredIcons.slice(i, i + ICONS_PER_ROW))
    }
    return result
  }, [filteredIcons])

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: 5,
  })

  const normalizedCurrent = normalizeIconName(currentIcon)

  return (
    <div className="flex flex-col">
      <div className="px-3 py-2">
        <Input
          placeholder={t('iconPicker.searchIcons')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 text-sm"
        />
      </div>
      <div
        ref={parentRef}
        className="h-[240px] overflow-auto px-3 pb-2 [scrollbar-width:thin]"
      >
        {filteredIcons.length === 0 ? (
          <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
            No icons found
          </div>
        ) : (
          <div
            className="relative w-full"
            style={{ height: `${virtualizer.getTotalSize()}px` }}
          >
            {virtualizer.getVirtualItems().map((virtualItem) => {
              const row = rows[virtualItem.index]
              // Index can momentarily exceed the row count while rows recompute.
              if (!row) return null
              return (
                <div
                  key={virtualItem.key}
                  className="absolute left-0 top-0 w-full"
                  style={{
                    height: `${virtualItem.size}px`,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  <div className="grid grid-cols-5 gap-0.5">
                    {row.map((iconData) => {
                      const name = iconData.name as IconName
                      const isActive = normalizedCurrent === name
                      return (
                        <TooltipProvider key={name}>
                          <Tooltip>
                            <TooltipTrigger
                              type="button"
                              className={cn(
                                'flex h-9 w-full items-center justify-center rounded-md text-muted-foreground transition-colors',
                                isActive
                                  ? 'bg-primary/10 text-primary'
                                  : 'hover:bg-muted/60',
                              )}
                              onClick={() => onSelect(name)}
                            >
                              <DynamicIcon name={name} className="size-4" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{name}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
